import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { inicioFimMesCorrenteLocal } from "../helpers/contas-receber-datas";
import { mapContasReceberApiToView } from "../helpers/map-contas-receber-api-to-view";
import { listarContasReceber } from "../services/contas-receber.service";
import type {
  ContasReceberApiResponse,
  ContasReceberFiltros,
  ContasReceberView,
} from "../types/contas-receber.types";

function filtrosPadrao(): ContasReceberFiltros {
  const { vencimentoInicio, vencimentoFim } = inicioFimMesCorrenteLocal();
  return {
    status: "",
    vencimentoInicio,
    vencimentoFim,
    origem: "",
    centroCustoId: "",
    pedidoId: "",
    busca: "",
    apenasVencidos: false,
    page: 1,
    limit: 20,
  };
}

export type UseContasReceberResult = {
  filtros: ContasReceberFiltros;
  setFiltros: Dispatch<SetStateAction<ContasReceberFiltros>>;
  filtrosAplicados: ContasReceberFiltros;
  data: ContasReceberView | null;
  rawData: ContasReceberApiResponse | null;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  isFiltered: boolean;
  page: number;
  limit: number;
  setPage: (p: number) => void;
  setLimit: (n: number) => void;
  setPagination: (page: number, limit: number) => void;
  aplicarFiltros: () => void;
  limparFiltros: () => void;
  refetch: () => void;
};

export function useContasReceber(): UseContasReceberResult {
  const [filtros, setFiltros] = useState<ContasReceberFiltros>(() =>
    filtrosPadrao()
  );
  const [filtrosAplicados, setFiltrosAplicados] =
    useState<ContasReceberFiltros>(() => filtrosPadrao());

  const [rawData, setRawData] = useState<ContasReceberApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const page = filtrosAplicados.page ?? 1;
  const limit = filtrosAplicados.limit ?? 20;

  const setPage = useCallback((p: number) => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      page: Math.max(1, p),
    }));
  }, []);

  const setLimit = useCallback((n: number) => {
    const nextLimit = Math.min(100, Math.max(1, n));
    setFiltrosAplicados((prev) => ({
      ...prev,
      limit: nextLimit,
      ...(prev.limit !== nextLimit ? { page: 1 } : {}),
    }));
  }, []);

  const setPagination = useCallback((p: number, n: number) => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      page: Math.max(1, p),
      limit: Math.min(100, Math.max(1, n)),
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload: ContasReceberFiltros = {
          ...filtrosAplicados,
          page: filtrosAplicados.page ?? 1,
          limit: filtrosAplicados.limit ?? 20,
        };
        const { data, status } = await listarContasReceber(payload);
        if (cancelled) return;
        if (status === 403) {
          setError("Sem permissão para listar contas a receber.");
          setRawData(null);
          return;
        }
        if (status === 404) {
          setError(
            "Endpoint de contas a receber não encontrado. Verifique se o backend expõe GET /loja/financeiro/titulos-receber."
          );
          setRawData(null);
          return;
        }
        if (status !== 200 || !data) {
          setError("Não foi possível carregar os títulos a receber.");
          setRawData(null);
          return;
        }
        setRawData(data);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e instanceof Error
            ? e.message
            : "Falha ao carregar contas a receber.";
        setError(msg);
        setRawData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [filtrosAplicados, tick]);

  const data = useMemo(() => mapContasReceberApiToView(rawData), [rawData]);

  const isEmpty = useMemo(() => {
    if (error) return false;
    return !loading && data != null && data.itens.length === 0;
  }, [error, loading, data]);

  const isFiltered = useMemo(() => {
    const defaults = filtrosPadrao();
    const f = filtrosAplicados;
    return (
      f.status !== defaults.status ||
      f.vencimentoInicio !== defaults.vencimentoInicio ||
      f.vencimentoFim !== defaults.vencimentoFim ||
      f.origem !== defaults.origem ||
      (f.busca ?? "").trim() !== "" ||
      f.apenasVencidos !== defaults.apenasVencidos
    );
  }, [filtrosAplicados]);

  const aplicarFiltros = useCallback(() => {
    setFiltrosAplicados({
      ...filtros,
      page: 1,
      limit: filtros.limit ?? 20,
    });
  }, [filtros]);

  const limparFiltros = useCallback(() => {
    const next = filtrosPadrao();
    setFiltros(next);
    setFiltrosAplicados(next);
  }, []);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  return {
    filtros,
    setFiltros,
    filtrosAplicados,
    data,
    rawData,
    loading,
    error,
    isEmpty,
    isFiltered,
    page,
    limit,
    setPage,
    setLimit,
    setPagination,
    aplicarFiltros,
    limparFiltros,
    refetch,
  };
}
